import type { CommonBlock, CommonData } from './blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from './blockEditor.types';

/**
 * Compares semantic version strings.
 * Returns -1 if a < b, 0 if a === b, 1 if a > b
 */
function compareVersions(a: string, b: string): number {
  const aParts = a.split('.').map(Number);
  const bParts = b.split('.').map(Number);
  const aMajor = aParts[0] ?? 0;
  const aMinor = aParts[1] ?? 0;
  const aPatch = aParts[2] ?? 0;
  const bMajor = bParts[0] ?? 0;
  const bMinor = bParts[1] ?? 0;
  const bPatch = bParts[2] ?? 0;

  if (aMajor !== bMajor) return aMajor < bMajor ? -1 : 1;
  if (aMinor !== bMinor) return aMinor < bMinor ? -1 : 1;
  if (aPatch !== bPatch) return aPatch < bPatch ? -1 : 1;
  return 0;
}

/**
 * Migration from 1.0.0 to 1.1.0:
 * Adds iconColor token to PresentationListItem blocks.
 * - flag and institution icon types get 'NEGATIVE'
 * - All other icon types get 'PRIMARY'
 */
function migrateFrom1_0_0To1_1_0(data: CommonData): CommonData {
  function migrateBlocks(blocks: CommonBlock[]): CommonBlock[] {
    return blocks.map((block) => {
      // Recursively migrate children (only if they exist)
      const migratedChildren = block.children
        ? Object.fromEntries(
            Object.entries(block.children).map(([key, childBlocks]) => [
              key,
              migrateBlocks(childBlocks),
            ])
          )
        : undefined;

      // Migrate PresentationListItem blocks - set iconColor based on iconType
      if (block.type === 'PresentationListItem') {
        const iconType = block.props.iconType as string | undefined;
        const iconColor =
          iconType === 'flag' || iconType === 'institution'
            ? 'NEGATIVE'
            : 'PRIMARY';

        return {
          ...block,
          props: { ...block.props, iconColor },
          // Only include children if they exist to preserve object shape for isEqual comparisons
          ...(migratedChildren && { children: migratedChildren }),
        };
      }

      return {
        ...block,
        // Only include children if they exist to preserve object shape for isEqual comparisons
        ...(migratedChildren && { children: migratedChildren }),
      };
    });
  }

  return {
    ...data,
    version: '1.1.0',
    blocks: migrateBlocks(data.blocks),
  };
}

/**
 * Migration from 1.1.0 to 1.2.0:
 * Removes SECONDARY icon color token, migrating to PRIMARY.
 * SECONDARY was removed as a color option.
 */
function migrateFrom1_1_0To1_2_0(data: CommonData): CommonData {
  function migrateBlocks(blocks: CommonBlock[]): CommonBlock[] {
    return blocks.map((block) => {
      // Recursively migrate children (only if they exist)
      const migratedChildren = block.children
        ? Object.fromEntries(
            Object.entries(block.children).map(([key, childBlocks]) => [
              key,
              migrateBlocks(childBlocks),
            ])
          )
        : undefined;

      // Migrate PresentationListItem blocks - convert SECONDARY to PRIMARY
      if (block.type === 'PresentationListItem') {
        const iconColor = block.props.iconColor as string | undefined;

        if (iconColor === 'SECONDARY') {
          return {
            ...block,
            props: { ...block.props, iconColor: 'PRIMARY' },
            ...(migratedChildren && { children: migratedChildren }),
          };
        }
      }

      return {
        ...block,
        // Only include children if they exist to preserve object shape for isEqual comparisons
        ...(migratedChildren && { children: migratedChildren }),
      };
    });
  }

  return {
    ...data,
    version: '1.2.0',
    blocks: migrateBlocks(data.blocks),
  };
}

/**
 * Migration from 1.2.0 to 1.3.0:
 * Converts EditableCallout from text prop to canvas with children.
 * The text prop is moved to an EditableEducationalSlideBody child block.
 */
function migrateFrom1_2_0To1_3_0(data: CommonData): CommonData {
  let idCounter = 0;

  function generateId(): string {
    idCounter += 1;
    return `migrated-callout-body-${idCounter}`;
  }

  function migrateBlocks(blocks: CommonBlock[]): CommonBlock[] {
    return blocks.map((block) => {
      // Recursively migrate children (only if they exist)
      const migratedChildren = block.children
        ? Object.fromEntries(
            Object.entries(block.children).map(([key, childBlocks]) => [
              key,
              migrateBlocks(childBlocks),
            ])
          )
        : undefined;

      // Migrate EditableCallout blocks - convert text prop to child block
      if (block.type === 'EditableCallout') {
        const text = block.props.text as string | undefined;

        // If there's a text prop, convert it to a child EditableEducationalSlideBody
        if (text !== undefined) {
          // Remove text from props
          const { text: _text, ...restProps } = block.props;

          // Create child body block with the text
          const bodyBlock: CommonBlock = {
            id: generateId(),
            type: 'EditableEducationalSlideBody',
            props: { text },
          };

          return {
            ...block,
            props: restProps,
            children: {
              default: [bodyBlock],
            },
          };
        }
      }

      return {
        ...block,
        // Only include children if they exist to preserve object shape for isEqual comparisons
        ...(migratedChildren && { children: migratedChildren }),
      };
    });
  }

  return {
    ...data,
    version: '1.3.0',
    blocks: migrateBlocks(data.blocks),
  };
}

/**
 * Migrates CommonData from any older version to CURRENT_SCHEMA_VERSION.
 * Apply migrations in sequence from the data's version to current.
 */
function migrateToCurrentVersion(data: CommonData): CommonData {
  let migrated = data;

  // Apply migrations in sequence
  if (compareVersions(migrated.version, '1.1.0') < 0) {
    migrated = migrateFrom1_0_0To1_1_0(migrated);
  }

  if (compareVersions(migrated.version, '1.2.0') < 0) {
    migrated = migrateFrom1_1_0To1_2_0(migrated);
  }

  if (compareVersions(migrated.version, '1.3.0') < 0) {
    migrated = migrateFrom1_2_0To1_3_0(migrated);
  }

  // Future migrations go here:
  // if (compareVersions(migrated.version, '1.4.0') < 0) {
  //   migrated = migrateFrom1_3_0To1_4_0(migrated);
  // }

  return migrated;
}

/**
 * Migrates already-parsed CommonData to the current schema version.
 * Use this when you have CommonData from a source that's already parsed (e.g., GraphQL).
 *
 * @param data - The CommonData object to migrate
 * @returns CommonData at CURRENT_SCHEMA_VERSION
 */
export function migrateCommonData(data: CommonData): CommonData {
  // Ensure version is set (handles legacy data without version field)
  if (!data.version) {
    data = { ...data, version: '1.0.0' };
  }

  // Already at current version, no migration needed
  if (data.version === CURRENT_SCHEMA_VERSION) {
    return data;
  }

  return migrateToCurrentVersion(data);
}
